'use client';

import { Button } from '@get-mosh/msh-shared';
import classNames from 'classnames';
import HTMLReactParser from 'html-react-parser';
import Image from 'next/image';
import { useEffect, useRef, useState } from 'react';
import common from 'src/data/common.json';
import { useIntersectionObserver } from 'src/hooks/useIntersectionObserver';
import { Image as ImageProps } from 'src/types/Image';
import FormulaCard, { FormulaCardProps } from '../FormulaCard';
import SnakePager from 'src/components/SnakePager';

import styles from './hero-formula.module.scss';
import { EventProps } from 'src/analytics/EventProps';
import { trackLandingPageButtonClick } from 'src/analytics/Events';
import Link from 'next/link';

export type HeroFormulaProps = {
    title: string;
    description?: string;
    slides: Array<{
        formula: FormulaCardProps;
        image: ImageProps;
    }>;
    cta: {
        label: string;
        link: string;
        external?: boolean;
        eventProps?: EventProps;
    };
    className?: string;
    children?: React.ReactNode;
};

const SLIDE_DURATION = 4000;

const HeroFormula = ({ title, slides, ...props }: HeroFormulaProps) => {
    const ref = useRef<HTMLDivElement>(null);
    const intersectedButtonRef = useRef<HTMLDivElement | null>(null);
    const [currentSlide, setCurrentSlide] = useState(0);

    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentSlide((currentSlide + 1) % slides.length);
        }, SLIDE_DURATION);
        return () => clearInterval(interval);
    }, [currentSlide]);

    const onScreen = useIntersectionObserver({
        ref,
        options: {
            threshold: 1,
            rootMargin: '0px 0px 50% 0px',
        },
    });

    const setButtonHeight = () => {
        const buttonHeight = intersectedButtonRef.current?.clientHeight;
        if (buttonHeight) {
            document
                .querySelector('html')
                ?.style.setProperty(
                    '--fixed-button-height',
                    buttonHeight + 'px',
                );
        }
    };

    useEffect(() => {
        window.addEventListener('resize', () => {
            setButtonHeight();
        });
    }, []);

    useEffect(() => {
        setButtonHeight();
    }, [onScreen]);

    const onClick = (location: 'StickyMobile' | 'Header') => {
        trackLandingPageButtonClick({
            location: location,
            label: props.cta.label,
            ...props.cta.eventProps,
        });
    };

    return (
        <div className={classNames(styles['section'])} data-hero>
            <div
                ref={ref}
                className={classNames(styles['content'], props.className)}
            >
                <h1>{HTMLReactParser(title)}</h1>
                {props.description && (
                    <p className={styles['description']}>{props.description}</p>
                )}
                {props.children && (
                    <div className={styles['children']}>{props.children}</div>
                )}
                <div className={styles['headroom-btm-wrapper']}>
                    {onScreen ? (
                        <>
                            {props.cta && (
                                <Button
                                    as={Link}
                                    text={common.takeTheQuiz}
                                    href={props.cta.link}
                                    target={
                                        props.cta.external ? '_blank' : '_self'
                                    }
                                    onClick={() => onClick('Header')}
                                    className="mb-0"
                                    stretch
                                />
                            )}
                        </>
                    ) : (
                        <>
                            {props.cta && (
                                <Button
                                    as={Link}
                                    text={common.takeTheQuiz}
                                    href={props.cta.link}
                                    target={
                                        props.cta.external ? '_blank' : '_self'
                                    }
                                    onClick={() => onClick('StickyMobile')}
                                    className="mb-0"
                                    stretch
                                />
                            )}
                        </>
                    )}
                </div>
            </div>
            <div className={styles['hero_formula']}>
                {slides.map((slide, index) => (
                    <div
                        key={index}
                        className={styles['slide']}
                        data-active={index === currentSlide}
                    >
                        <Image
                            src={slide.image.src}
                            alt={title}
                            fill
                            priority
                            sizes="(max-width: 768px) 100vw, 50vw"
                            className={styles['hero_formula-hero']}
                        />
                        <div className={styles['hero_formula-wrapper']}>
                            <FormulaCard {...slide.formula} />
                            {slides.length > 1 ? (
                                <SnakePager
                                    className={styles['hero_formula-pager']}
                                    page={currentSlide}
                                    pages={slides.length}
                                    timer={SLIDE_DURATION}
                                />
                            ) : null}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default HeroFormula;
